import { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useLocation } from "@tanstack/react-router";
import { Flex, Text } from "styles/utility-components";

import { ParagonCrmProvider } from "components/integrations/managed_integrations/crmProviders";
import { providerIconColorMap, providerIconKeyMap } from "components/integrations/ProviderButtons";
import Button from "components/ui/button/Button";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import toast from "components/ui/toast/Toast";
import Tooltip from "components/ui/tooltip/Tooltip";
import { FieldMappingData, MappableField } from "lib/generated/integration-api";
import { useDescribeMappableEntity } from "lib/hooks/api/integrations/useDescribeMappableEntity";
import {
  rejectionReason,
  useSaveFieldMapping,
} from "lib/hooks/api/integrations/useSaveFieldMapping";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { CrmPermissionRefused } from "lib/integrations/crmSendErrors";
import {
  acceptsCrmField,
  fieldMappingSectionId,
  holdsAPerson,
  OBJECT_TYPE_BY_KIND,
  RECORD_SECTION_TITLES,
  recordFieldsFor,
} from "lib/integrations/paragonCrm/fieldCompatibility";
import { CrmRecordKind, RecordFieldSpec } from "lib/integrations/paragonCrm/recordFields";
import { EventNames, useTracking } from "lib/tracking";
import CrmFieldCombobox from "./CrmFieldCombobox";
import MappingCombobox from "./MappingCombobox";
import { useMappingDraft } from "./useMappingDraft";

import stotlesLogo from "../../../../assets/images/stotles_logo.svg";

type Props = {
  kind: CrmRecordKind;
  /** The CRM the company has connected, which decides what a reference may point at. */
  provider: ParagonCrmProvider;
  /** The CRM objects a record can be pushed to. */
  entities: string[];
  /** What was configured before, if anything. */
  saved: FieldMappingData | undefined;
};

/**
 * Configures where one kind of Stotles record is written in the CRM: which object, and which of
 * its fields each of our own fields goes to.
 *
 * Our fields are fixed — they are the data a record carries — so each is a row, and the only
 * choice is the CRM field beside it.
 */
export default function FieldMappingSection({
  kind,
  provider,
  entities,
  saved,
}: Props): JSX.Element {
  const { logEvent } = useTracking();

  // Only the objects this record may become. The service refuses the rest on save and on every
  // push, so offering them would let a company configure something that can never send.
  const offered = useMemo(
    () => entities.filter((entity) => provider.mappableObjects[kind].includes(entity)),
    [entities, kind, provider.mappableObjects],
  );
  const sectionId = fieldMappingSectionId(kind);
  const section = useRef<HTMLDivElement>(null);
  const hash = useLocation({ select: (location) => location.hash });

  // The page renders behind two requests, so the router's own hash scroll runs while this section
  // is still a spinner. Brought into view here instead, once there is something to look at.
  useEffect(() => {
    if (hash !== sectionId) return;
    section.current?.scrollIntoView({ block: "start" });
  }, [hash, sectionId]);

  const [entityName, setEntityName] = useState(saved?.entityName ?? "");

  // A mapping saved before the rule narrowed. Kept in the list because the control reads its label
  // from there: without it the trigger shows a placeholder and the page looks unconfigured, while
  // the fields below and every push still use the saved object.
  const savedButNotOffered = !!entityName && !offered.includes(entityName);
  const selectable = savedButNotOffered ? [entityName, ...offered] : offered;

  // The object decides this as much as the record: a lead written to a CRM lead describes a person
  // too, one written to an opportunity has nobody to describe.
  const specs = useMemo(
    () => recordFieldsFor(kind, provider.lookupTargets, holdsAPerson(provider, entityName)),
    [kind, provider, entityName],
  );

  // Split by where the values come from. Not for a contact, whose own details are the record and
  // would leave the heading over the whole section.
  const [aboutTheRecord, aboutThePerson] = useMemo(() => {
    if (kind === "contact") return [specs, []];
    return [
      specs.filter((spec) => !spec.aboutThePerson),
      specs.filter((spec) => spec.aboutThePerson),
    ];
  }, [specs, kind]);

  // A section that has never been configured starts at the object this record usually goes to,
  // falling back to whatever the CRM offers first when it does not have that one.
  useEffect(() => {
    if (entityName || offered.length === 0) return;
    const preferred = provider.defaultObjects[kind];
    setEntityName(offered.includes(preferred) ? preferred : offered[0]);
  }, [offered, entityName, kind, provider.defaultObjects]);

  const { data, isFetching, error } = useDescribeMappableEntity(entityName, offered.length > 0);
  const crmFields = data?.entity.fields;

  // Worth telling apart: a retry cannot grant a scope, so "try again" is the one instruction here
  // that can never work.
  const permissionRefused = error instanceof CrmPermissionRefused;

  const { mapping, chooseMapping, isSuggested, clear, markSaved } = useMappingDraft({
    provider,
    kind,
    entityName,
    saved,
    specs,
    crmFields,
  });

  const { mutate: save, isLoading: isSaving } = useSaveFieldMapping({
    onSuccess: () => {
      markSaved();
      toast.success(`${RECORD_SECTION_TITLES[kind]} mapping saved.`);
    },
    onError: async (saveError) => {
      // The service checks the mapping against the CRM, so its message names the field at fault —
      // far more use than a generic failure.
      const reason = await rejectionReason(saveError);
      toast.error(reason || `Could not save the ${RECORD_SECTION_TITLES[kind]} mapping.`);
    },
  });

  const handleSave = () => {
    logEvent(EventNames.settingsSavedForIntegration, {
      "Page source": "Integration Configuration",
      "Connected provider": "DYNAMICS",
      "Stotles object": kind,
    });
    save({
      stotlesObjectType: OBJECT_TYPE_BY_KIND[kind],
      entityName,
      fields: Object.entries(mapping).map(([stotlesField, crmField]) => ({
        stotlesField,
        crmField,
      })),
    });
  };

  const unmapped = (crmFields ?? []).filter(
    (field) => field.required && !Object.values(mapping).includes(field.id),
  );
  // Told apart because only one of them is a rule. A push is refused without the first; the second
  // is marked required for a reason that is not the create's — Dynamics' own forms demand it, or
  // the create path supplies a default — and the record is made either way.
  const unmappedRequired = unmapped.filter((field) => field.requiredOnCreate);
  const unmappedRecommended = unmapped.filter((field) => !field.requiredOnCreate);

  return (
    <Section
      ref={section}
      id={sectionId}
      aria-label={`${RECORD_SECTION_TITLES[kind]} field mapping`}
    >
      {/* Whose names are in each column. Said once per section rather than badged on every row:
          the columns are fixed, and the middle cell is left empty because the arrow belongs to the
          rows that actually map something. */}
      <Row>
        <ColumnHeading>
          <StotlesMark src={stotlesLogo} alt="" />
          <Text description>Stotles</Text>
        </ColumnHeading>
        <Spacer />
        <ColumnHeading>
          <UIcon
            icon={providerIconKeyMap[provider.providerType]}
            size={14}
            color={providerIconColorMap[provider.providerType]}
          />
          <Text description>{provider.name}</Text>
        </ColumnHeading>
      </Row>

      <Row>
        <Text h3>{RECORD_SECTION_TITLES[kind]}</Text>
        <UIcon icon="arrowNarrowRight" size={16} color="sysTextSecondary" />
        <MappingCombobox
          options={selectable.map((entity) => ({ value: entity, label: objectLabel(entity) }))}
          value={entityName || undefined}
          onChange={(next) => {
            // The fields belong to the object, so a mapping made against another is meaningless.
            setEntityName(next ?? "");
            clear();
          }}
          label={`${RECORD_SECTION_TITLES[kind]} CRM object`}
          placeholder="Select an object"
          searchPlaceholder="Search objects"
          emptyMessage="No matching objects"
        />
      </Row>

      {savedButNotOffered && (
        <Text color="sysDestructiveDefault">
          {RECORD_SECTION_TITLES[kind]}s can no longer be mapped to {objectLabel(entityName)}.
          Choose another object and save — until then, sending a{" "}
          {RECORD_SECTION_TITLES[kind].toLowerCase()} will be refused.
        </Text>
      )}

      {!!error && permissionRefused && (
        <Text color="sysDestructiveDefault">
          Your {provider.name} connection has not been given permission to read{" "}
          {objectLabel(entityName)}. Grant it in {provider.name}, then reconnect from the
          integrations page.
        </Text>
      )}

      {!!error && !permissionRefused && (
        <Text color="sysDestructiveDefault">
          Could not read the fields of {objectLabel(entityName)} from your CRM. Please try again.
        </Text>
      )}

      {isFetching && !crmFields && (
        <Rows>
          {specs.map((spec) => (
            <Row key={spec.key}>
              <Text>{spec.label}</Text>
              <UIcon icon="arrowNarrowRight" size={16} color="sysTextSecondary" />
              <SkeletonText width="360px" />
            </Row>
          ))}
        </Rows>
      )}

      {crmFields && (
        <>
          <FieldGroup
            heading={
              aboutThePerson.length > 0
                ? `About this ${RECORD_SECTION_TITLES[kind].toLowerCase()}`
                : undefined
            }
            group={aboutTheRecord}
            crmFields={crmFields}
            mapping={mapping}
            entityName={entityName}
            onChange={chooseMapping}
          />
          {aboutThePerson.length > 0 && (
            <FieldGroup
              heading="About the person"
              description={`${objectLabel(entityName)} in your CRM describes a person as well as the ${RECORD_SECTION_TITLES[
                kind
              ].toLowerCase()}, so these are the fields it has for one.`}
              group={aboutThePerson}
              crmFields={crmFields}
              mapping={mapping}
              entityName={entityName}
              onChange={chooseMapping}
            />
          )}
        </>
      )}

      {unmappedRequired.length > 0 && (
        <Text color="sysDestructiveDefault">
          Your CRM will not create a record without{" "}
          {unmappedRequired.map((field) => field.name).join(", ")}. Map{" "}
          {unmappedRequired.length === 1 ? "it" : "them"} here, or the send form will ask for{" "}
          {unmappedRequired.length === 1 ? "it" : "them"} every time.
        </Text>
      )}

      {unmappedRecommended.length > 0 && (
        <Text description>
          Your CRM marks {unmappedRecommended.map((field) => field.name).join(", ")} as required,
          though it creates records without {unmappedRecommended.length === 1 ? "it" : "them"}. Map{" "}
          {unmappedRecommended.length === 1 ? "it" : "them"} here to set the value yourself rather
          than leave it to the CRM.
        </Text>
      )}

      {isSuggested && (
        <Text description data-testid="crm-suggested-mapping">
          These are suggestions, filled in from the fields your CRM has. Nothing is sent to{" "}
          {provider.name} until you save them, so change anything that does not look right first.
        </Text>
      )}

      <Flex justifyContent="flex-end">
        <Button
          aria-label={`Save ${RECORD_SECTION_TITLES[kind]} mapping`}
          onClick={handleSave}
          loading={isSaving}
          disabled={!entityName || !crmFields}
        >
          Save {RECORD_SECTION_TITLES[kind]} mapping
        </Button>
      </Flex>
    </Section>
  );
}

/**
 * One group of rows. Extracted so the two groups cannot drift: the only thing that differs between
 * them is where their values come from, which the heading says and the rows do not.
 */
function FieldGroup({
  heading,
  description,
  group,
  crmFields,
  mapping,
  entityName,
  onChange,
}: {
  heading?: string;
  description?: string;
  group: RecordFieldSpec[];
  crmFields: MappableField[];
  mapping: Record<string, string>;
  entityName: string;
  onChange: (update: (current: Record<string, string>) => Record<string, string>) => void;
}): JSX.Element {
  return (
    <>
      {heading && (
        <Flex column gap={4}>
          {/* A real heading, subordinate to the section's own h3: bold text alone reads as a
              heading to the eye but is skipped by heading navigation. */}
          <Text as="h4" bold>
            {heading}
          </Text>
          {description && <Text description>{description}</Text>}
        </Flex>
      )}
      <Rows>
        {group.map((spec) => {
          const available = crmFields.filter((field) => acceptsCrmField(spec, field));
          const chosen = mapping[spec.key];
          return (
            <Row key={spec.key}>
              <Flex alignItems="center" gap={4}>
                <Text>{spec.label}</Text>
                {available.length === 0 && (
                  <Tooltip
                    content={noCompatibleFieldsMessage(spec.control, objectLabel(entityName))}
                  >
                    <span>
                      <UIcon icon="alertCircle" size={14} color="sysTextSecondary" />
                    </span>
                  </Tooltip>
                )}
              </Flex>
              <UIcon icon="arrowNarrowRight" size={16} color="sysTextSecondary" />
              <CrmFieldCombobox
                label={spec.label}
                fields={available}
                value={chosen}
                onChange={(crmField) =>
                  onChange((current) => {
                    const next = { ...current };
                    if (crmField) next[spec.key] = crmField;
                    else delete next[spec.key];
                    return next;
                  })
                }
              />
            </Row>
          );
        })}
      </Rows>
    </>
  );
}

/** Dynamics names its objects in lower case; the form is the only place a person reads them. */
function objectLabel(entityName: string): string {
  return entityName.charAt(0).toUpperCase() + entityName.slice(1);
}

function noCompatibleFieldsMessage(control: string, entityName: string): string {
  if (control === "account" || control === "owner") {
    return `${entityName} has no field that can link to a record of this kind, so this cannot be mapped.`;
  }
  return `${entityName} has no field of a matching type, so this cannot be mapped.`;
}

const Section = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 16,
  padding: 24,
  borderRadius: 8,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  backgroundColor: theme.colors.sysBackgroundAlternative,
}));

/** The two sides named, in the muted register of a column heading rather than a field's own label. */
const ColumnHeading = styled.div({
  display: "flex",
  alignItems: "center",
  gap: 6,
});

/** Sized to the CRM icon beside it, so neither side is the louder of the two. */
const StotlesMark = styled.img({
  width: 14,
  height: 14,
  display: "block",
});

const Rows = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

// Our own field on the left and the CRM's on the right, in the direction the data travels. The
// trailing column is bounded rather than fluid so a wide screen does not strand the two apart.
const Row = styled.div({
  display: "grid",
  // `max-content` rather than `auto` for the arrow: an auto track absorbs the row's spare width,
  // which pushed the two columns to opposite edges of the card.
  gridTemplateColumns: "minmax(0, 180px) max-content minmax(0, 360px)",
  alignItems: "center",
  gap: 12,
});

const Spacer = styled.span({
  width: 16,
});
